import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Calendar, MapPin, BarChart3, ArrowLeft } from "lucide-react";
import { getVisaDaysT } from "@/data/visaDaysI18n";
import { PostcodeChecker } from "./PostcodeChecker";
import { EligibleZonesMap } from "./EligibleZonesMap";
import { Tracker88Days } from "./Tracker88Days";

// Short description per tab — helps users understand what each does
const TAB_HINTS = {
  en: {
    tracker: "Count your 88 days and export the proof PDF",
    checker: "Check if a postcode is eligible for 88 days",
    map: "Browse eligible zones by state and industry",
  },
  fr: {
    tracker: "Compter tes 88 jours et exporter la preuve PDF",
    checker: "Vérifier si un code postal est éligible aux 88 jours",
    map: "Parcourir les zones éligibles par état et secteur",
  },
  de: {
    tracker: "Zähle deine 88 Tage und exportiere den Nachweis (PDF)",
    checker: "Prüfe, ob eine Postleitzahl für die 88 Tage zählt",
    map: "Berechtigte Zonen nach Bundesstaat und Branche",
  },
  it: {
    tracker: "Conta i tuoi 88 giorni ed esporta la prova in PDF",
    checker: "Verifica se un codice postale è idoneo agli 88 giorni",
    map: "Sfoglia le zone idonee per stato e settore",
  },
  es: {
    tracker: "Cuenta tus 88 días y exporta el PDF de prueba",
    checker: "Verifica si un código postal es elegible para los 88 días",
    map: "Explora zonas elegibles por estado y sector",
  },
  pt: {
    tracker: "Conta os teus 88 dias e exporta o PDF de prova",
    checker: "Verifica se um código postal é elegível para os 88 dias",
    map: "Explora zonas elegíveis por estado e setor",
  },
};

export function VisaDaysPage({ lang, onBack, user }) {
  const t = getVisaDaysT(lang);
  const hints = TAB_HINTS[lang] || TAB_HINTS.en;
  // Default to "tracker" — the most-used flow (counting days). Users can still switch.
  const [tab, setTab] = useState("tracker");

  const tabs = [
    { key: "tracker", label: t.tabs.tracker, icon: Calendar },
    { key: "checker", label: t.tabs.checker, icon: MapPin },
    { key: "map", label: t.tabs.map, icon: BarChart3 },
  ];
  const currentTab = tabs.find((tb) => tb.key === tab);

  return (
    <main data-testid="visa-days-page">
      {/* ============ COMPACT HERO ============ */}
      <section className="relative border-b border-[#EAE5DB] bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-4 lg:pt-8 pb-3 lg:pb-4">
          <button
            onClick={onBack}
            className="inline-flex items-center gap-2 text-xs sm:text-sm text-[#78716C] hover:text-[#D95D39] transition-colors mb-3 sm:mb-4"
            data-testid="visa-days-back"
          >
            <ArrowLeft className="w-4 h-4" /> {t.back_to_app}
          </button>

          <div className="flex items-start justify-between gap-6">
            <div className="min-w-0 flex-1">
              <div className="overline mb-1.5">{t.overline}</div>
              <h1 className="font-heading font-extrabold text-[28px] sm:text-4xl lg:text-5xl leading-[1.05] text-[#D95D39] uppercase tracking-tight mb-2">
                {t.title}
              </h1>
              <p className="text-[#57534E] text-sm sm:text-base lg:text-lg max-w-2xl leading-snug">
                {t.subtitle}
              </p>
            </div>

            {/* Compact 88 badge — desktop only */}
            <div className="hidden lg:flex flex-shrink-0 bg-[#292524] text-white rounded-2xl px-5 py-3 items-center gap-3 shadow-lg">
              <Calendar className="w-6 h-6 text-[#E5A93D]" />
              <div>
                <div className="overline !text-[#E5A93D] text-[10px] leading-none mb-1">DAYS</div>
                <div className="font-heading font-extrabold text-3xl leading-none">88</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============ STICKY TAB BAR — always accessible ============ */}
      <div
        className="sticky top-14 sm:top-16 z-30 bg-white border-b border-[#EAE5DB] shadow-sm"
        data-testid="visa-days-tabs"
      >
        <div className="max-w-7xl mx-auto px-2 sm:px-6 lg:px-8">
          <div className="flex items-center gap-1 sm:gap-2 overflow-x-auto">
            {tabs.map((tb) => {
              const Icon = tb.icon;
              const active = tab === tb.key;
              return (
                <button
                  key={tb.key}
                  onClick={() => setTab(tb.key)}
                  className={`relative inline-flex items-center gap-2 px-3 sm:px-6 py-3 text-xs sm:text-sm font-bold whitespace-nowrap transition-colors ${
                    active ? "text-[#D95D39]" : "text-[#78716C] hover:text-[#292524]"
                  }`}
                  data-testid={`visa-tab-${tb.key}`}
                >
                  <Icon className="w-4 h-4 flex-shrink-0" />
                  {tb.label}
                  {active && (
                    <motion.div
                      layoutId="active-visa-tab"
                      className="absolute -bottom-px left-0 right-0 h-[3px] bg-[#D95D39] rounded-t-full"
                    />
                  )}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* ============ TAB HINT — 1-line explanation of what this tab does ============ */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-4 pb-1">
        <div
          className="inline-flex items-center gap-2 text-[11px] sm:text-xs text-[#78716C] bg-[#FAF6F0] border border-[#EAE5DB] rounded-full px-3 py-1.5"
          data-testid="visa-tab-hint"
        >
          {currentTab && <currentTab.icon className="w-3.5 h-3.5 flex-shrink-0 text-[#D95D39]" />}
          <span>{hints[tab]}</span>
        </div>
      </div>

      {/* ============ TAB CONTENT ============ */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-4 pb-10 lg:pb-14">
        <AnimatePresence mode="wait">
          <motion.div
            key={tab}
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.25 }}
          >
            {tab === "checker" && <PostcodeChecker t={t} lang={lang} />}
            {tab === "map" && <EligibleZonesMap t={t} />}
            {tab === "tracker" && <Tracker88Days t={t} lang={lang} user={user} />}
          </motion.div>
        </AnimatePresence>
      </div>
    </main>
  );
}
